import api from './api';

export interface ParticipantReadiness {
  userId: string;
  isReady: boolean;
  readyAt?: Date;
  lastSeen: Date;
}

export interface CompetitionReadiness {
  _id: string;
  competitionId: string;
  roomId: string;
  participants: ParticipantReadiness[];
  status: 'waiting' | 'ready' | 'starting' | 'started';
  countdownStartTime?: Date;
  countdownEndTime?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface ReadinessUpdateData {
  roomId: string;
  isReady: boolean;
}

export interface InitializeReadinessData {
  competitionId: string;
  roomId: string;
  participantIds: string[];
}

class CompetitionReadinessService {
  private baseUrl = '/api/competition-readiness';

  /**
   * Initialize readiness tracking for a new competition
   */
  async initializeReadiness(data: InitializeReadinessData): Promise<CompetitionReadiness> {
    try {
      const response = await api.post(`${this.baseUrl}/initialize`, data);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to initialize competition readiness');
      }
    } catch (error) {
      console.error('Error initializing competition readiness:', error);
      throw error;
    }
  }

  /**
   * Update participant readiness status
   */
  async updateReadiness(data: ReadinessUpdateData): Promise<CompetitionReadiness> {
    try {
      const response = await api.post(`${this.baseUrl}/update`, data);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update readiness status');
      }
    } catch (error) {
      console.error('Error updating readiness status:', error);
      throw error;
    }
  }

  /**
   * Get current readiness status for a room
   */
  async getReadinessStatus(roomId: string): Promise<CompetitionReadiness> {
    try {
      const response = await api.get(`${this.baseUrl}/status/${roomId}`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to get readiness status');
      }
    } catch (error) {
      console.error('Error getting readiness status:', error);
      throw error;
    }
  }

  /**
   * Start countdown when all participants are ready
   */
  async startCountdown(roomId: string): Promise<void> {
    try {
      const response = await api.post(`${this.baseUrl}/start-countdown`, { roomId });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to start countdown');
      }
    } catch (error) {
      console.error('Error starting countdown:', error);
      throw error;
    }
  }

  /**
   * Mark competition as started
   */
  async markCompetitionStarted(roomId: string): Promise<void> {
    try {
      const response = await api.post(`${this.baseUrl}/mark-started`, { roomId });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to mark competition as started');
      }
    } catch (error) {
      console.error('Error marking competition as started:', error);
      throw error;
    }
  }

  /**
   * Reset readiness for a new round
   */
  async resetReadiness(roomId: string): Promise<void> {
    try {
      const response = await api.post(`${this.baseUrl}/reset`, { roomId });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to reset readiness');
      }
    } catch (error) {
      console.error('Error resetting readiness:', error);
      throw error;
    }
  }

  /**
   * Clean up readiness tracking
   */
  async cleanupReadiness(roomId: string): Promise<void> {
    try {
      const response = await api.delete(`${this.baseUrl}/cleanup`, { data: { roomId } });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to clean up readiness tracking');
      }
    } catch (error) {
      console.error('Error cleaning up readiness tracking:', error);
      throw error;
    }
  }

  /**
   * Poll readiness status for real-time updates
   */
  startReadinessPolling(roomId: string, callback: (readiness: CompetitionReadiness) => void, interval = 2000): () => void {
    let isPolling = true;
    
    const poll = async () => {
      if (!isPolling) return;
      
      try {
        const readiness = await this.getReadinessStatus(roomId);
        callback(readiness);
        
        // Stop polling if competition has started
        if (readiness.status === 'started') {
          isPolling = false;
          return;
        }
      } catch (error) {
        console.error('Error polling readiness status:', error);
      }
      
      if (isPolling) {
        setTimeout(poll, interval);
      }
    };
    
    poll();
    
    // Return function to stop polling
    return () => {
      isPolling = false;
    };
  }
}

export default new CompetitionReadinessService();
